/**
 * Sorting a column the backend cannot sort yet. Strict list endpoints answer a
 * `sort` outside their whitelist with 400 "sort field is invalid"; the client
 * then repeats the request without `sort` (the endpoint's default order) and
 * tells the reader once, so a table never breaks over a header click. When the
 * backend adds the field, the first request succeeds and nothing of this runs.
 * The fields still missing on the backend are listed in docs/backend/table-sorting.md.
 */

const UNSUPPORTED = new Set(['sort field is invalid', 'sort type is invalid'])

export function isUnsupportedSort(status: number | undefined, message: unknown, params: unknown): boolean {
  return status === 400 && typeof message === 'string' && UNSUPPORTED.has(message.trim()) && hasSort(params)
}

function hasSort(params: unknown): boolean {
  return Boolean(params && typeof params === 'object' && 'sort' in params && (params as Record<string, unknown>).sort)
}

/** The same params without `sort`. */
export function withoutSort<T>(params: T): T {
  const rest = { ...(params as Record<string, unknown>) }
  delete rest.sort
  return rest as T
}

type Listener = (path: string) => void
const listeners = new Set<Listener>()
/** Paths already reported, so a page that refetches does not repeat the notice. */
const reported = new Set<string>()

/** Subscribes to «this column cannot be sorted yet»; returns the unsubscribe. */
export function onUnsupportedSort(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function reportUnsupportedSort(path: string, sort: unknown) {
  const key = `${path} ${String(sort)}`
  if (reported.has(key)) return
  reported.add(key)
  for (const listener of listeners) listener(path)
}
